import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import toast from "react-hot-toast";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import {
  BenefitsMetricsKpiCards,
  BenefitsMetricsLoadingOverlay,
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "./components/BenefitsMetricsWidgets";
import {
  BenefitMetricsTopBenefit,
  BenefitMetricsTopPartner,
} from "../../models/benefitsMetrics";
import { TENANT_CONTEXT_ALL } from "../../store/services/api";
import { useGetBenefitMetricsQuery } from "../../store/services/benefits";
import { useBenefitsMetricsExport } from "../../hooks/useBenefitsMetricsExport";
import { useTenantBrandColors } from "../../hooks/useTenantBrandColors";
import { useTenantScope } from "../../hooks/useTenantScope";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import {
  defaultBenefitsMetricsDateFrom,
  defaultBenefitsMetricsDateTo,
  endOfDayIso,
  startOfDayIso,
} from "../../utils/functions/benefitsMetricsDateRange";
import {
  benefitsMetricsChartOptions,
  benefitsMetricsTenantDayChartOptions,
  buildBenefitsMetricsDetailPath,
  buildBenefitsMetricsTenantSeries,
  buildRedemptionsByDayChartData,
  buildTenantRedemptionsByDayChartData,
  hasBenefitsMetricsTenantBreakdown,
} from "../../utils/functions/benefitsMetricsPresentation";
import { useBenefitsMetricsQueryDisplay } from "../../hooks/useBenefitsMetricsQueryDisplay";
import { getTenantBrandColor } from "../../utils/functions/tenantThemeColor";

export const BenefitsMetricsDashboard: React.FC = () => {
  const scope = useTenantScope();
  const selectedTenantId = scope.tenantId;
  const [dateFrom, setDateFrom] = useState<Date | null>(defaultBenefitsMetricsDateFrom);
  const [dateTo, setDateTo] = useState<Date | null>(defaultBenefitsMetricsDateTo);
  const [todayDate] = useState(defaultBenefitsMetricsDateTo);

  const queryParams = {
    ...(dateFrom ? { dateFrom: startOfDayIso(dateFrom) } : {}),
    ...(dateTo ? { dateTo: endOfDayIso(dateTo) } : {}),
    ...(selectedTenantId ? { tenantId: selectedTenantId } : {}),
  };

  const { data, currentData, isLoading, isFetching, isError, error } =
    useGetBenefitMetricsQuery(queryParams, { skip: scope.loading });
  const { metrics, isBlockingLoading, isRefreshing } = useBenefitsMetricsQueryDisplay({
    data,
    currentData,
    isFetching,
    scopeKey: selectedTenantId ?? TENANT_CONTEXT_ALL,
  });
  const { exportMetrics, isExporting } = useBenefitsMetricsExport();

  useEffect(() => {
    if (isError) {
      toast.error(getApiErrorMessage(error) ?? "Error al cargar métricas");
    }
  }, [isError, error]);

  const redemptionsByDay = useMemo(
    () => metrics?.redemptions_by_day ?? [],
    [metrics?.redemptions_by_day]
  );
  const tenantBrandColors = useTenantBrandColors();
  const tenantSeries = useMemo(
    () => buildBenefitsMetricsTenantSeries(metrics?.by_tenant),
    [metrics?.by_tenant]
  );
  const showTenantBreakdown =
    !selectedTenantId && hasBenefitsMetricsTenantBreakdown(tenantSeries);
  const selectedTenantColor = selectedTenantId
    ? tenantBrandColors[selectedTenantId] ?? getTenantBrandColor(null)
    : null;
  const chartData = useMemo(
    () =>
      showTenantBreakdown
        ? buildTenantRedemptionsByDayChartData(tenantSeries)
        : buildRedemptionsByDayChartData(redemptionsByDay, selectedTenantColor, {
            dateFrom,
            dateTo,
          }),
    [showTenantBreakdown, tenantSeries, redemptionsByDay, selectedTenantColor, dateFrom, dateTo]
  );
  const emptyStateMessage = isError
    ? "No pudimos cargar las métricas"
    : "Sin canjes en el rango seleccionado";

  const detailPath = buildBenefitsMetricsDetailPath({
    dateFrom: dateFrom ? startOfDayIso(dateFrom) : undefined,
    dateTo: dateTo ? endOfDayIso(dateTo) : undefined,
  });

  const benefitTitleBody = (row: BenefitMetricsTopBenefit) => row.title ?? row.id;

  const tenantNameBody = (row: BenefitMetricsTopBenefit | BenefitMetricsTopPartner) =>
    row.tenant_name ?? row.tenant_id;

  const partnerNameBody = (row: BenefitMetricsTopPartner) => (
    <Link
      to={buildBenefitsMetricsDetailPath({
        partnerId: row.id,
        dateFrom: dateFrom ? startOfDayIso(dateFrom) : undefined,
        dateTo: dateTo ? endOfDayIso(dateTo) : undefined,
      })}
      state={{ partnerName: row.name }}
      className="text-primary no-underline hover:underline"
    >
      {row.name ?? row.id}
    </Link>
  );

  return (
    <div className="p-4">
      <div className="p-card p-4 relative overflow-hidden">
        <BenefitsMetricsLoadingOverlay visible={scope.loading || isBlockingLoading} />

        <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

        <div className="mb-4 flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
          <div className="flex align-items-center gap-2">
            <h1 className="text-2xl font-semibold text-900 m-0">Métricas de beneficios</h1>
            <BenefitsMetricsRefreshingIndicator visible={isRefreshing} />
          </div>
          <div className="flex align-items-center gap-3">
            <Button
              type="button"
              label="Exportar CSV"
              icon="pi pi-download"
              onClick={() => exportMetrics(queryParams)}
              disabled={scope.loading || isBlockingLoading || isExporting}
              loading={isExporting}
              data-testid="export-benefits-metrics-button"
            />
            <Link to={detailPath} className="text-primary no-underline hover:underline">
              Ver detalle
            </Link>
          </div>
        </div>

        {/* Por encima del overlay: en una carga fria el dato queda tapado, pero el
            usuario tiene que poder seguir moviendo los filtros. */}
        <div className="grid mb-4 relative" style={{ maxWidth: "480px", zIndex: 11 }}>
          <DateRangeFilter
            variant="stacked"
            columnClassName="col-12 sm:col-6"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            disabled={isLoading}
            startDateId="benefits-metrics-date-from"
            endDateId="benefits-metrics-date-to"
            maxDateFrom={todayDate}
            maxDateTo={todayDate}
            minDateTo={dateFrom ?? undefined}
            showClearButtons
          />
        </div>

        <BenefitsMetricsKpiCards
          totalActiveBenefits={metrics?.total_active_benefits}
          totalRedemptions={metrics?.total_redemptions}
        />

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-900 mt-0 mb-3">Canjes por día</h2>
          {redemptionsByDay.length ? (
            <div style={{ height: showTenantBreakdown ? "320px" : "280px" }}>
              <Chart
                key={`benefits-metrics-day-${selectedTenantId ?? "all"}-${selectedTenantColor ?? "default"}`}
                type="line"
                data={chartData}
                options={
                  showTenantBreakdown
                    ? benefitsMetricsTenantDayChartOptions
                    : benefitsMetricsChartOptions
                }
                className="h-full w-full"
              />
            </div>
          ) : (
            <p className="text-color-secondary m-0">
              {isBlockingLoading ? "" : emptyStateMessage}
            </p>
          )}
        </div>

        <div className="grid">
          <div className="col-12 md:col-6">
            <h2 className="text-xl font-semibold text-900 mt-0 mb-3">Top beneficios</h2>
            <DataTable
              value={metrics?.top_benefits ?? []}
              dataKey="id"
              emptyMessage="Sin datos"
              size="small"
            >
              <Column header="Título" body={benefitTitleBody} />
              {showTenantBreakdown ? (
                <Column header="Tenant" body={tenantNameBody} />
              ) : null}
              <Column field="redemptions" header="Canjes" />
            </DataTable>
          </div>
          <div className="col-12 md:col-6">
            <h2 className="text-xl font-semibold text-900 mt-0 mb-3">Top partners</h2>
            <DataTable
              value={metrics?.top_partners ?? []}
              dataKey="id"
              emptyMessage="Sin datos"
              size="small"
            >
              <Column header="Nombre" body={partnerNameBody} />
              {showTenantBreakdown ? (
                <Column header="Tenant" body={tenantNameBody} />
              ) : null}
              <Column field="redemptions" header="Canjes" />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
};
